import { Component, useEffect, useState } from "react";
import { onUpdateAvailable } from "@/pwa";

// Small banners for the installable app: "Install Sendtoo" (Android/desktop install prompt, or how to add it on
// iPhone) and "New version available". Shown at the top so they never cover booking buttons at the bottom.
const DISMISS_KEY = "sendtoo_install_dismissed_at";
const DISMISS_DAYS = 14;
const get = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const set = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };
const standalone = () => window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const recentlyDismissed = () => { const t = Number(get(DISMISS_KEY)); return t && Date.now() - t < DISMISS_DAYS * 86400000; };

function Bar({ children, onClose, testid }) {
  return (
    <div className="fixed top-3 inset-x-3 sm:left-auto sm:right-4 sm:w-[380px] z-[60] rounded-2xl border border-slate-200 bg-white shadow-xl p-3 flex items-center gap-3" data-testid={testid}>
      <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-xl border border-slate-100 shrink-0" />
      <div className="min-w-0 flex-1 text-sm">{children}</div>
      {onClose && <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 text-lg leading-none px-1">×</button>}
    </div>
  );
}

function Banners() {
  const [prompt, setPrompt] = useState(null);
  const [ios, setIos] = useState(false);
  const [update, setUpdate] = useState(false);

  useEffect(() => {
    if (standalone() || recentlyDismissed()) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => { setPrompt(null); set(DISMISS_KEY, String(Date.now())); };
    window.addEventListener("appinstalled", onInstalled);
    let t;
    if (isIos()) t = setTimeout(() => setIos(true), 8000);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); clearTimeout(t); };
  }, []);
  useEffect(() => onUpdateAvailable(() => setUpdate(true)), []);

  const dismiss = () => { set(DISMISS_KEY, String(Date.now())); setPrompt(null); setIos(false); };
  const install = async () => {
    const p = prompt; setPrompt(null);
    try { p.prompt(); const r = await p.userChoice; if (r?.outcome !== "accepted") set(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  if (update) return (
    <Bar testid="pwa-update" onClose={() => setUpdate(false)}>
      <p className="font-semibold text-slate-900">New version of Sendtoo</p>
      <p className="text-xs text-slate-500">Save your work, then refresh to update.</p>
      <button onClick={() => window.location.reload()} className="mt-1.5 h-8 px-3 rounded-lg bg-brand-500 text-white text-xs font-semibold">Refresh now</button>
    </Bar>
  );
  if (prompt) return (
    <Bar testid="pwa-install" onClose={dismiss}>
      <p className="font-semibold text-slate-900">Install the Sendtoo app</p>
      <p className="text-xs text-slate-500">Book and track from your home screen.</p>
      <button onClick={install} className="mt-1.5 h-8 px-3 rounded-lg bg-brand-500 text-white text-xs font-semibold">Install</button>
    </Bar>
  );
  if (ios) return (
    <Bar testid="pwa-install-ios" onClose={dismiss}>
      <p className="font-semibold text-slate-900">Add Sendtoo to your Home Screen</p>
      <p className="text-xs text-slate-500">Tap the Share button <span aria-hidden>⎋</span> in Safari, then <b>Add to Home Screen</b>.</p>
    </Bar>
  );
  return null;
}

// never let a banner problem show up as an error in the app
class Guard extends Component {
  constructor(p) { super(p); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { /* ignore */ }
  render() { return this.state.failed ? null : this.props.children; }
}

export default function AppBanners() {
  return <Guard><Banners /></Guard>;
}
